'use client';

import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { orderBy } from 'es-toolkit/compat';

import { Section } from '@/models';
import { GUEST_PORTAL_IMAGE_QUALITY } from '@/constants/images';
import SectionTitle from '../theme/SectionTitle';
import { NavLink } from '../navigation/Navigation';

import 'swiper/css';
import Image from 'next/image';
import { VStack, Text, Box } from '@ui-library';

const MULTI_SLIDE_IMAGE_SIZES =
  '(max-width: 768px) 60vw, (max-width: 1200px) 400px, 500px';
const SINGLE_SLIDE_IMAGE_SIZES =
  '(max-width: 768px) 100vw, (max-width: 1200px) 67vw, 800px';
const SINGLE_SLIDE_MAX_IMAGE_HEIGHT = '360px';
const SINGLE_SLIDE_IMAGE_ASPECT_RATIO = '4 / 3';

interface SliderProps {
  section: Section;
  company: string;
  guestBookSlug: string;
  isKioskMode?: boolean;
}

const Slider = ({
  section,
  company,
  guestBookSlug,
  isKioskMode,
}: SliderProps) => {
  const hasMultipleSlides = section.blocks.length > 1;
  const orderedBlocks = orderBy(section.blocks, (b, i) =>
    b.settings.hasOwnProperty('index') ? b.settings.index : i,
  );
  return (
    <Box p="4">
      {/* Heading */}
      <SectionTitle>{section.plain_text}</SectionTitle>

      {/* Slides */}
      <Box display="flex" mt="4">
        <Swiper
          style={{ width: '100%' }}
          spaceBetween={hasMultipleSlides ? 10 : 0}
          slidesPerView={hasMultipleSlides ? 1.8 : 1}
        >
          {orderedBlocks.map((slide) => {
            const src = slide.settings.thumbnail as string;
            const alt = src || 'Slide image';
            return (
              <SwiperSlide key={slide.id}>
                <Box borderRadius="0.5rem" overflow="hidden" w="100%">
                  <NavLink
                    block={slide}
                    company={company}
                    guestBookSlug={guestBookSlug}
                    isKioskMode={isKioskMode}
                  >
                    <VStack gap="2" alignItems="center" w="100%">
                      <Box
                        position="relative"
                        w="100%"
                        h={hasMultipleSlides ? '180px' : undefined}
                        minH={hasMultipleSlides ? '180px' : undefined}
                        aspectRatio={
                          hasMultipleSlides
                            ? undefined
                            : SINGLE_SLIDE_IMAGE_ASPECT_RATIO
                        }
                        maxH={
                          hasMultipleSlides
                            ? undefined
                            : SINGLE_SLIDE_MAX_IMAGE_HEIGHT
                        }
                      >
                        <Image
                          src={src}
                          alt={alt}
                          fill
                          sizes={
                            hasMultipleSlides
                              ? MULTI_SLIDE_IMAGE_SIZES
                              : SINGLE_SLIDE_IMAGE_SIZES
                          }
                          style={{
                            objectFit: 'contain',
                            objectPosition: 'center',
                          }}
                          quality={GUEST_PORTAL_IMAGE_QUALITY}
                        />
                      </Box>
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        px="2"
                        pb="2"
                        textAlign="center"
                      >
                        {slide.plain_text}
                      </Text>
                    </VStack>
                  </NavLink>
                </Box>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </Box>
    </Box>
  );
};

export default Slider;
